import { createAuthClient } from "better-auth/react";
import { organizationClient } from "better-auth/client/plugins";
import type { AuthQueryAtom } from "better-auth/client";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

export const authClient = createAuthClient({
  baseURL: `${API_BASE_URL}/api/auth`,
  plugins: [
    organizationClient({
      schema: {
        organization: {
          additionalFields: {
            tenantId: {
              type: "string",
              required: true,
            },
            parentId: {
              type: "string",
              required: false,
            },
          },
        },
      },
    }),
  ],
});

// Lives here (rather than useAuth.ts) so hydrateSession() below can return
// it without a circular import; kept as the hook's public type surface.
export interface AuthSession {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
  };
  // Real `/get-session` responses are `{ session, user }` or `null`
  // wholesale (node_modules/better-auth/dist/api/routes/session.mjs) —
  // never a user without a session — so this is required, not optional.
  session: {
    activeOrganizationId?: string | null;
  };
}

export const SESSION_HYDRATION_FAILED_MESSAGE =
  "Signed in, but your session couldn't be loaded — please refresh the page.";

export const SIGN_OUT_HYDRATION_FAILED_MESSAGE =
  "Signed out, but this page still holds the previous session — please refresh the page.";

export const ORG_SWITCH_HYDRATION_FAILED_MESSAGE =
  "Organization switched, but the session couldn't be refreshed — please refresh the page.";

/** Bound on how long hydrateSession() will wait for `/get-session` to settle. */
export const HYDRATE_SESSION_TIMEOUT_MS = 10_000;

// Test-only: lets tests exercise the REAL timeout path through
// signOut()/setActiveOrganization() (which call hydrateSession() with no
// per-call opts) without waiting out the real 10s production default.
// Production code never calls this setter.
let timeoutOverrideForTests: number | undefined;
export function setHydrateSessionTimeoutForTests(ms: number | undefined): void {
  timeoutOverrideForTests = ms;
}

// better-auth's client typed `$store.atoms` as `Record<string, WritableAtom<any>>`
// (see node_modules/better-auth/dist/client/react/index.d.mts). This is the
// one place that narrows that `any` down to the shape the `/get-session`
// `useAuthQuery` atom actually carries, shared by hydrateSession() and
// readSession() below.
function getSessionAtom(): AuthQueryAtom<AuthSession> {
  const sessionAtom = authClient.$store.atoms.session as
    | AuthQueryAtom<AuthSession>
    | undefined;

  // Loud, un-swallowed failure: if a future better-auth upgrade changes this
  // internal shape, callers must see a clear "hydrateSession() needs
  // updating" error, not one of the user-facing "couldn't be loaded"
  // messages below (those mean "the network/session hydration failed", not
  // "our code is broken").
  if (
    typeof sessionAtom?.get !== "function" ||
    typeof sessionAtom.get().refetch !== "function"
  ) {
    throw new Error(
      "better-auth session atom not found — hydrateSession() needs updating for this better-auth version"
    );
  }

  return sessionAtom;
}

/**
 * Re-fetch `/get-session` into better-auth's session store and resolve once
 * it settles.
 *
 * FEAT-004: better-auth's client proxy only flips the `$sessionSignal` atom
 * in `setTimeout(() => signal.set(!val), 10)` after a successful sign-in/
 * sign-up (node_modules/better-auth/dist/client/proxy.mjs "to avoid race
 * conditions"), so `authClient.useSession()` can still report the
 * pre-login `{ data: null }` immediately after `authClient.signIn.email(...)`
 * / `signUp.email(...)` resolves. `authClient.$store.atoms.session` is the
 * real `useAuthQuery` atom backing that store; its current value carries an
 * awaitable `refetch()` that performs the same `/get-session` request the
 * 10ms signal would eventually trigger. Calling it directly here lets
 * callers await session hydration deterministically instead of racing a
 * timer.
 *
 * IMPORTANT (Codex review, FEAT-004 round 4): this function's return value
 * collapses two different situations into the same `null` — "the refetch
 * settled and there is no session" AND "the refetch timed out, and the
 * store's PREVIOUS state (which may still hold a signed-in user) was left
 * untouched". Callers that need to distinguish those — e.g. signOut()
 * verifying the user is actually gone, or setActiveOrganization()
 * verifying the org actually changed — must read `readSession()` (the
 * store's current value) after awaiting this, not trust this return value
 * alone.
 */
export async function hydrateSession(opts?: {
  /** Overrides HYDRATE_SESSION_TIMEOUT_MS — test-only injection point; production callers omit this. */
  timeoutMs?: number;
}): Promise<AuthSession | null> {
  const timeoutMs =
    opts?.timeoutMs ?? timeoutOverrideForTests ?? HYDRATE_SESSION_TIMEOUT_MS;
  const sessionAtom = getSessionAtom();

  // useAuthQuery's `fn` (node_modules/better-auth/dist/client/query.mjs)
  // catches every fetch failure into the atom's own `error` field inside a
  // `.catch()`/`.finally()` chain and always resolves its returned promise —
  // `refetch()` itself never rejects. Bound the wait with our own timer
  // instead, so a hung request can't hang signIn/signUp forever.
  let timedOut = false;
  const refetch = sessionAtom.get().refetch();
  await Promise.race([
    refetch,
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        timedOut = true;
        resolve();
      }, timeoutMs);
      // Clear the timer once refetch actually settles, whichever wins.
      refetch.finally(() => clearTimeout(timer));
    }),
  ]);

  if (timedOut) {
    return null;
  }

  const { data, error } = sessionAtom.get();
  if (error) {
    return null;
  }
  return data ?? null;
}

/**
 * Synchronous read of the session store's CURRENT value — no network call.
 * Use this (not hydrateSession()'s return value) to verify what the store
 * actually holds after awaiting hydrateSession(): on a timeout, the store
 * still holds whatever it held before the refetch was attempted, which
 * hydrateSession() reports as `null` regardless (see its doc comment
 * above).
 */
export function readSession(): AuthSession | null {
  const sessionAtom = getSessionAtom();
  const { data, error } = sessionAtom.get();
  if (error) {
    return null;
  }
  return data ?? null;
}
